import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import STOCK_EMAILS from './stockEmails';
import './CampaignManager.css';

const API_BASE = 'https://hjjtpav7x0.execute-api.us-east-1.amazonaws.com/prod';

const CampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, detail, builder
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [enrollLeadQrCode, setEnrollLeadQrCode] = useState('');
  const [expandedSteps, setExpandedSteps] = useState({});
  const [showEmailLibrary, setShowEmailLibrary] = useState(false);
  const [libraryPreview, setLibraryPreview] = useState(null);

  // Builder form state
  const [builderForm, setBuilderForm] = useState({
    name: '',
    description: '',
    status: 'draft',
    autoEnroll: false,
    steps: []
  });

  const getToken = async () => {
    const session = await fetchAuthSession();
    return session.tokens.idToken.toString();
  };

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEnrollments = async (campaignId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns/${campaignId}/enrollments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEnrollments(data);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/leads?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Campaign CRUD
  const saveCampaign = async () => {
    try {
      const token = await getToken();
      const isEdit = !!selectedCampaign;
      const url = isEdit
        ? `${API_BASE}/admin/campaigns/${selectedCampaign.campaignId}`
        : `${API_BASE}/admin/campaigns`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(builderForm)
      });

      if (response.ok) {
        await fetchCampaigns();
        setView('list');
        setSelectedCampaign(null);
      } else {
        alert('Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCampaigns();
      } else {
        alert('Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns/${campaign.campaignId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Error toggling campaign status:', error);
    }
  };

  const toggleAutoEnroll = async (campaign) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns/${campaign.campaignId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ autoEnroll: !campaign.autoEnroll })
      });
      if (response.ok) {
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Error toggling auto-enroll:', error);
    }
  };

  // Enrollment actions
  const updateEnrollmentStatus = async (campaignId, qrCode, enrollmentStatus) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns/${campaignId}/enrollments/${encodeURIComponent(qrCode)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enrollmentStatus })
      });
      if (response.ok) {
        fetchEnrollments(campaignId);
      } else {
        alert('Failed to update enrollment');
      }
    } catch (error) {
      console.error('Error updating enrollment:', error);
    }
  };

  const manualEnroll = async (campaignId) => {
    if (!enrollLeadQrCode) {
      alert('Please select a lead');
      return;
    }
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/admin/campaigns/${campaignId}/enrollments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ qrCode: enrollLeadQrCode })
      });
      if (response.ok) {
        setEnrollLeadQrCode('');
        fetchEnrollments(campaignId);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to enroll lead');
      }
    } catch (error) {
      console.error('Error enrolling lead:', error);
    }
  };

  // View transitions
  const openDetail = (campaign) => {
    setSelectedCampaign(campaign);
    setView('detail');
    fetchEnrollments(campaign.campaignId);
    fetchLeads();
  };

  const openBuilder = (campaign = null) => {
    if (campaign) {
      setSelectedCampaign(campaign);
      setBuilderForm({
        name: campaign.name,
        description: campaign.description || '',
        status: campaign.status,
        autoEnroll: campaign.autoEnroll || false,
        steps: campaign.steps || []
      });
    } else {
      setSelectedCampaign(null);
      setBuilderForm({
        name: '',
        description: '',
        status: 'draft',
        autoEnroll: false,
        steps: []
      });
    }
    setView('builder');
  };

  const backToList = () => {
    setView('list');
    setSelectedCampaign(null);
    setEnrollments([]);
  };

  // Steps editor helpers
  const addStep = () => {
    const newStep = {
      stepIndex: builderForm.steps.length,
      delayDays: builderForm.steps.length > 0 ? (builderForm.steps[builderForm.steps.length - 1].delayDays + 7) : 0,
      type: 'email',
      name: '',
      subject: '',
      content: ''
    };
    setBuilderForm({ ...builderForm, steps: [...builderForm.steps, newStep] });
  };

  const removeStep = (index) => {
    const steps = builderForm.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, stepIndex: i }));
    setBuilderForm({ ...builderForm, steps });
  };

  const updateStep = (index, field, value) => {
    const steps = [...builderForm.steps];
    steps[index] = { ...steps[index], [field]: field === 'delayDays' ? parseInt(value) || 0 : value };
    setBuilderForm({ ...builderForm, steps });
  };

  const moveStep = (index, direction) => {
    const steps = [...builderForm.steps];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    const reindexed = steps.map((s, i) => ({ ...s, stepIndex: i }));
    setBuilderForm({ ...builderForm, steps: reindexed });
  };

  const addStockEmail = (stockEmail) => {
    const lastStep = builderForm.steps[builderForm.steps.length - 1];
    const newStep = {
      stepIndex: builderForm.steps.length,
      delayDays: lastStep ? lastStep.delayDays + 7 : 0,
      type: stockEmail.type,
      name: stockEmail.name,
      subject: stockEmail.subject,
      content: stockEmail.content
    };
    setBuilderForm({ ...builderForm, steps: [...builderForm.steps, newStep] });
    setShowEmailLibrary(false);
  };

  const getStepTypeIcon = (type) => {
    switch (type) {
      case 'email': return '📧';
      case 'call_reminder': return '📞';
      case 'sms': return '💬';
      default: return '📋';
    }
  };

  // ===== RENDER =====

  if (loading) {
    return <div className="campaign-loading">Loading campaigns...</div>;
  }

  // LIST VIEW
  if (view === 'list') {
    return (
      <div className="campaign-manager">
        <div className="campaign-header">
          <h2>Campaigns</h2>
          <button className="btn-primary" onClick={() => openBuilder()}>Create Campaign</button>
        </div>

        {campaigns.length === 0 ? (
          <div className="campaign-empty">
            <p>No campaigns yet. Create your first campaign to get started.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="campaign-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Steps</th>
                  <th>Enrollments</th>
                  <th>Auto-Enroll</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr key={campaign.campaignId}>
                    <td>
                      <button className="back-link" onClick={() => openDetail(campaign)}>
                        {campaign.name}
                      </button>
                    </td>
                    <td>
                      <span className={`status-badge ${campaign.status}`}>{campaign.status}</span>
                    </td>
                    <td>{(campaign.steps || []).length}</td>
                    <td>{campaign.enrollmentCount || 0}</td>
                    <td>
                      <div className="auto-enroll-toggle">
                        <input
                          type="checkbox"
                          checked={campaign.autoEnroll || false}
                          onChange={() => toggleAutoEnroll(campaign)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="campaign-actions">
                        <button className="btn-primary btn-small" onClick={() => openBuilder(campaign)}>Edit</button>
                        <button className="btn-secondary btn-small" onClick={() => openDetail(campaign)}>View</button>
                        <button
                          className="btn-secondary btn-small"
                          onClick={() => toggleCampaignStatus(campaign)}
                        >
                          {campaign.status === 'active' ? 'Pause' : 'Activate'}
                        </button>
                        <button className="btn-danger btn-small" onClick={() => deleteCampaign(campaign.campaignId)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && selectedCampaign) {
    return (
      <div className="campaign-manager campaign-detail">
        <button className="back-link" onClick={backToList}>&larr; Back to Campaigns</button>

        <div className="campaign-detail-header">
          <div>
            <h2>{selectedCampaign.name}</h2>
            {selectedCampaign.description && <p>{selectedCampaign.description}</p>}
            <div className="campaign-meta">
              <span className={`status-badge ${selectedCampaign.status}`}>{selectedCampaign.status}</span>
              <span>{(selectedCampaign.steps || []).length} steps</span>
              <span>{selectedCampaign.autoEnroll ? 'Auto-enroll ON' : 'Auto-enroll OFF'}</span>
            </div>
          </div>
          <button className="btn-primary" onClick={() => openBuilder(selectedCampaign)}>Edit Campaign</button>
        </div>

        {/* Step Timeline */}
        <div className="step-timeline">
          <h3>Campaign Steps</h3>
          {(selectedCampaign.steps || []).map((step, i) => (
            <div key={i} className={`step-item ${step.type}`}>
              <span className="step-day">Day {step.delayDays}</span>
              <span className="step-type-icon">{getStepTypeIcon(step.type)}</span>
              <div className="step-info">
                <div className="step-name">
                  {step.name || `Step ${i + 1}`}
                  {step.content && (
                    <button
                      className="expand-step-btn"
                      onClick={() => setExpandedSteps(prev => ({ ...prev, [i]: !prev[i] }))}
                    >
                      {expandedSteps[i] ? '▼ Hide' : '► Preview'}
                    </button>
                  )}
                </div>
                {step.subject && <div className="step-subject">Subject: {step.subject}</div>}
                {expandedSteps[i] && step.content && (
                  <div className="step-content-preview" dangerouslySetInnerHTML={{ __html: step.content }} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Enrollments */}
        <div className="enrollments-section">
          <h3>
            <span>Enrollments ({enrollments.length})</span>
          </h3>

          <div className="manual-enroll">
            <select
              value={enrollLeadQrCode}
              onChange={(e) => setEnrollLeadQrCode(e.target.value)}
            >
              <option value="">Select a lead to enroll...</option>
              {leads
                .filter(l => l.email)
                .map(l => (
                  <option key={l.qrCode} value={l.qrCode}>
                    {l.firstName || ''} {l.lastName || ''} — {l.email || l.qrCode}
                  </option>
                ))
              }
            </select>
            <button
              className="btn-primary btn-small"
              onClick={() => manualEnroll(selectedCampaign.campaignId)}
              disabled={!enrollLeadQrCode}
            >
              Enroll Lead
            </button>
          </div>

          {enrollments.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="campaign-table">
                <thead>
                  <tr>
                    <th>Lead</th>
                    <th>Status</th>
                    <th>Current Step</th>
                    <th>Next Send</th>
                    <th>Enrolled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map(enrollment => {
                    const step = (selectedCampaign.steps || [])[enrollment.currentStepIndex];
                    return (
                      <tr key={enrollment.qrCode}>
                        <td>{enrollment.qrCode}</td>
                        <td>
                          <span className={`status-badge ${enrollment.enrollmentStatus}`}>
                            {enrollment.enrollmentStatus}
                          </span>
                        </td>
                        <td>
                          {step ? `${enrollment.currentStepIndex}: ${step.name}` : `Step ${enrollment.currentStepIndex}`}
                        </td>
                        <td>
                          {enrollment.nextSendDate && enrollment.nextSendDate !== '9999-12-31T23:59:59Z'
                            ? new Date(enrollment.nextSendDate).toLocaleString()
                            : '—'}
                        </td>
                        <td>{new Date(enrollment.enrolledAt).toLocaleDateString()}</td>
                        <td>
                          <div className="enrollment-actions">
                            {enrollment.enrollmentStatus === 'active' && (
                              <button
                                className="btn-secondary btn-small"
                                onClick={() => updateEnrollmentStatus(selectedCampaign.campaignId, enrollment.qrCode, 'paused')}
                              >
                                Pause
                              </button>
                            )}
                            {enrollment.enrollmentStatus === 'paused' && (
                              <button
                                className="btn-primary btn-small"
                                onClick={() => updateEnrollmentStatus(selectedCampaign.campaignId, enrollment.qrCode, 'active')}
                              >
                                Resume
                              </button>
                            )}
                            {enrollment.enrollmentStatus !== 'cancelled' && enrollment.enrollmentStatus !== 'completed' && (
                              <button
                                className="btn-danger btn-small"
                                onClick={() => updateEnrollmentStatus(selectedCampaign.campaignId, enrollment.qrCode, 'cancelled')}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#666' }}>No enrollments yet.</p>
          )}
        </div>
      </div>
    );
  }

  // BUILDER VIEW
  if (view === 'builder') {
    return (
      <div className="campaign-manager campaign-builder">
        <button className="back-link" onClick={backToList}>&larr; Back to Campaigns</button>
        <h2>{selectedCampaign ? 'Edit Campaign' : 'Create Campaign'}</h2>

        <div className="builder-form">
          <div className="form-group">
            <label>Campaign Name</label>
            <input
              type="text"
              value={builderForm.name}
              onChange={(e) => setBuilderForm({ ...builderForm, name: e.target.value })}
              placeholder="e.g., Renter to Homeowner (6-Month)"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={builderForm.description}
              onChange={(e) => setBuilderForm({ ...builderForm, description: e.target.value })}
              placeholder="Describe the campaign purpose..."
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                value={builderForm.status}
                onChange={(e) => setBuilderForm({ ...builderForm, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="form-check">
            <input
              type="checkbox"
              id="autoEnroll"
              checked={builderForm.autoEnroll}
              onChange={(e) => setBuilderForm({ ...builderForm, autoEnroll: e.target.checked })}
            />
            <label htmlFor="autoEnroll">Auto-enroll new leads when they submit the contact form</label>
          </div>

          {/* Steps Editor */}
          <div className="steps-editor">
            <h3>Campaign Steps ({builderForm.steps.length})</h3>

            {builderForm.steps.map((step, index) => (
              <div key={index} className="step-editor-item">
                <div className="step-editor-header">
                  <span>Step {index + 1}</span>
                  <div className="step-editor-actions">
                    <button onClick={() => moveStep(index, -1)} disabled={index === 0} title="Move up">&uarr;</button>
                    <button onClick={() => moveStep(index, 1)} disabled={index === builderForm.steps.length - 1} title="Move down">&darr;</button>
                    <button onClick={() => removeStep(index)} title="Remove step">&times;</button>
                  </div>
                </div>

                <div className="step-editor-fields">
                  <div>
                    <select
                      value={step.type}
                      onChange={(e) => updateStep(index, 'type', e.target.value)}
                    >
                      <option value="email">Email</option>
                      <option value="call_reminder">Call Reminder</option>
                      <option value="sms">SMS (Coming Soon)</option>
                    </select>
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Step name"
                      value={step.name}
                      onChange={(e) => updateStep(index, 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      placeholder="Day"
                      value={step.delayDays}
                      onChange={(e) => updateStep(index, 'delayDays', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                {(step.type === 'email' || step.type === 'call_reminder') && (
                  <div className="step-content-fields">
                    <input
                      type="text"
                      placeholder="Email subject line"
                      value={step.subject || ''}
                      onChange={(e) => updateStep(index, 'subject', e.target.value)}
                    />
                    <textarea
                      placeholder="Email content (HTML supported)"
                      value={step.content || ''}
                      onChange={(e) => updateStep(index, 'content', e.target.value)}
                    />
                    <div className="variable-help">
                      Variables: {'{firstName}'}, {'{lastName}'}, {'{email}'}, {'{phone}'}, {'{address}'}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="add-step-actions">
              <button className="btn-secondary add-step-btn" onClick={addStep}>
                + Add Blank Step
              </button>
              <button className="btn-primary add-step-btn" onClick={() => setShowEmailLibrary(true)}>
                + Add from Email Library
              </button>
            </div>

            {showEmailLibrary && (
              <div className="email-library-overlay" onClick={() => { setShowEmailLibrary(false); setLibraryPreview(null); }}>
                <div className="email-library-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="email-library-header">
                    <h3>Email Library</h3>
                    <button className="btn-secondary btn-small" onClick={() => { setShowEmailLibrary(false); setLibraryPreview(null); }}>Close</button>
                  </div>
                  <p className="email-library-subtitle">Select a pre-written email to add as a campaign step.</p>
                  <div className="email-library-list">
                    {STOCK_EMAILS.map((email, i) => (
                      <div key={i} className={`email-library-item ${libraryPreview === i ? 'selected' : ''}`}>
                        <div className="email-library-item-header" onClick={() => setLibraryPreview(libraryPreview === i ? null : i)}>
                          <span className="email-library-icon">{getStepTypeIcon(email.type)}</span>
                          <div className="email-library-info">
                            <div className="email-library-name">{email.name}</div>
                            <div className="email-library-subject">{email.subject}</div>
                          </div>
                          <span className="email-library-type">{email.type === 'call_reminder' ? 'Call' : email.type}</span>
                        </div>
                        {libraryPreview === i && (
                          <div className="email-library-preview">
                            <div className="email-library-preview-content" dangerouslySetInnerHTML={{ __html: email.content }} />
                            <button className="btn-primary" onClick={() => addStockEmail(email)}>
                              Add to Campaign
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="builder-actions">
            <button className="btn-primary" onClick={saveCampaign}>
              {selectedCampaign ? 'Update Campaign' : 'Create Campaign'}
            </button>
            <button className="btn-secondary" onClick={backToList}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CampaignManager;
